import { useRef } from 'react';
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Box,
  Button,
  Hidden,
} from '@material-ui/core';
import { Delete } from '@material-ui/icons';
import {
  DndProvider,
  useDrop,
  useDrag,
  DragLayerMonitor,
  XYCoord,
} from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import update from 'immutability-helper';

import useStyles from './styles';
import { useCars } from '../../hooks/useCars';
import { formatMoney } from '../../../../utils/formatters/formatMoney';
import { FleetsService } from '../../../../services';
import clsx from 'clsx';
import { assetUrl } from '../../../../utils';

type MoveProps = {
  dragIndex: number;
  hoverIndex: number;
};

const TableCars = () => {
  const classes = useStyles();
  const { hasOrder, removeCarSelected, carSelected, setCarSelectedContext } =
    useCars();

  const isMobile = window.innerWidth < 600;

  const moveRow = ({ dragIndex, hoverIndex }: MoveProps) => {
    const dragRecord = carSelected[dragIndex];
    setCarSelectedContext(
      update(carSelected, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, dragRecord],
        ],
      })
    );
  };

  const handleRemoveCar = async (vehicleId: number) => {
    if (!hasOrder) {
      removeCarSelected(vehicleId);
      return;
    }
    const orderId = (carSelected[0] as any).orderId;
    await FleetsService.removeVehicle(orderId, vehicleId);
    removeCarSelected(vehicleId);
  };

  return (
    <DndProvider backend={isMobile ? TouchBackend : HTML5Backend}>
      <TableContainer>
        <Table className={classes.table}>
          <TableHead>
            <TableRow classes={{ root: classes.tr }}>
              <TableCell className={classes.th}>Modelo</TableCell>
              <TableCell className={classes.th}>Valor</TableCell>
              <TableCell className={classes.th}>Prioridade</TableCell>
              <Hidden smDown>
                <TableCell />
              </Hidden>
            </TableRow>
          </TableHead>
          <TableBody>
            {carSelected.map((row, index) => (
              <Row
                key={`car-${row.id}`}
                index={index}
                row={row}
                moveRow={moveRow}
                classes={classes}
                removeCar={handleRemoveCar}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </DndProvider>
  );
};

const DND_ITEM_TYPE = 'row';

const Row = ({ row, index, moveRow, classes, removeCar }: any) => {
  const ref = useRef<HTMLTableRowElement>(null);

  const [, dropRef] = useDrop({
    accept: DND_ITEM_TYPE,
    hover(item: any, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const targetIndex = index;

      if (dragIndex === targetIndex) {
        return;
      }

      const hoverBoudingRect = ref.current?.getBoundingClientRect();

      const hoverMiddleY =
        ((hoverBoudingRect as DOMRect).bottom -
          (hoverBoudingRect as DOMRect).top) /
        2;

      const clientOffset = monitor.getClientOffset() as XYCoord;

      const hoverClientY = clientOffset?.y - (hoverBoudingRect as DOMRect)?.top;

      if (dragIndex < targetIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > targetIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      moveRow({ dragIndex, hoverIndex: targetIndex });
      item.index = targetIndex;
    },
  });

  const [{ isDragging }, dragRef] = useDrag({
    type: DND_ITEM_TYPE,
    item: { type: DND_ITEM_TYPE, index },
    collect: (monitor: DragLayerMonitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const opacity = isDragging ? 0 : 1;

  dragRef(dropRef(ref));

  return (
    <TableRow ref={ref} classes={{ root: classes.tr }} style={{ opacity }}>
      <TableCell>
        <Box className={classes.cell}>
          <img
            className={classes.img}
            src={assetUrl(row.thumbnail)}
            alt={row.name}
            onClick={() => {}}
          />
          <Box display="flex" flexDirection="column">
            <p className={classes.name}>{row.name}</p>
            <Box display="flex">
              <strong className={classes.brand}>{row.brand}</strong>
              <p className={classes.description}>{row.transmission}</p>
            </Box>
            <p className={classes.description}>
              {row.year}/{row.yearModel}
            </p>
          </Box>
        </Box>
      </TableCell>
      <TableCell>
        <p className={classes.price}>{formatMoney(row.price)}</p>
      </TableCell>
      <Box className={clsx(classes.cell, classes.cellSpaced)}>
        <TableCell>
          <p className={classes.priority}>{index + 1}°</p>
        </TableCell>
        <TableCell>
          <Button onClick={() => removeCar(row.id)}>
            <Delete className={classes.deleteButton} />
          </Button>
        </TableCell>
      </Box>
    </TableRow>
  );
};

export default TableCars;
